import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { EcondosQuery } from '@api/model/query';
import { ProductBanner, ProductBannerRole } from '@api/model/product-banner';
import { ProductBannerService } from '@api/service/product-banner.service';

const ROLE_LABELS: Record<ProductBannerRole, string> = {
  OWNER: 'Síndico',
  ADMIN: 'Administrador',
  USER: 'Morador',
  GATEKEEPER: 'Porteiro',
  JANITOR: 'Zelador'
};

// Linha da tabela com os rótulos já resolvidos (evita função em binding de leitura).
type BannerRow = ProductBanner & { productLabelText: string; rolesText: string };

@Component({
  selector: 'app-banners-page',
  templateUrl: './banners-page.component.html',
  styleUrls: ['./banners-page.component.scss']
})
export class BannersPageComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  rows: BannerRow[] = [];
  isLoading = false;

  constructor(
    private productBannerService: ProductBannerService,
    private router: Router,
    private notification: NzNotificationService
  ) {}

  ngOnInit(): void {
    this.loadBanners();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  loadBanners(): void {
    this.isLoading = true;
    const query: EcondosQuery = {
      $select: 'title product userTypes isActive position updatedAt',
      $sort: 'position'
    };

    forkJoin({
      topics: this.productBannerService.getTopics(),
      result: this.productBannerService.getProductBanners(query)
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ({ topics, result }) => {
          const labels = topics.reduce((acc, topic) => ({ ...acc, [topic.value]: topic.label }), {} as Record<string, string>);
          this.rows = result.banners.map(banner => ({
            ...banner,
            productLabelText: labels[banner.product] || banner.product,
            rolesText: this.buildRolesText(banner.userTypes)
          }));
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.notification.create('error', 'Erro', 'Não foi possível carregar os banners.');
        }
      });
  }

  private buildRolesText(userTypes: ProductBannerRole[]): string {
    if (!userTypes || !userTypes.length) {
      return 'Todos';
    }
    return userTypes.map(role => ROLE_LABELS[role] || role).join(', ');
  }

  goToCreate(): void {
    this.router.navigate(['/home/marketplace/banners/new']);
  }

  goToEdit(banner: ProductBanner): void {
    this.router.navigate(['/home/marketplace/banners', banner._id]);
  }

  deleteBanner(banner: ProductBanner): void {
    this.productBannerService
      .deleteProductBanner(banner._id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.notification.create('success', 'Banner', 'Banner excluído com sucesso.');
          this.loadBanners();
        },
        error: () => this.notification.create('error', 'Erro', 'Não foi possível excluir o banner.')
      });
  }
}
